import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
import React, { useEffect, useMemo, useState } from "react";
import { BASE_URL } from "../../../config/api";
import { ValueLoader } from "../../../Component/Common/ValueLoader";

/* ---------- inline loader helper ---------- */
const InlineLoader = ({ width = "w-8" }) => (
  <span className={`inline-block align-middle ${width}`}>
    <ValueLoader />
  </span>
);

export default function ReportsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const location_mode = authUser?.location_mode;
  const state = authUser?.state;
  const district = authUser?.district;

  /* ================= BUILD API URL ================= */
  const apiUrl = useMemo(() => {
    if (location_mode === "state" && state) {
      return `${BASE_URL}company/employee-dashboard?state=${state}`;
    }

    if (location_mode === "district" && district) {
      return `${BASE_URL}company/employee-dashboard?district=${district}`;
    }

    return null;
  }, [location_mode, state, district]);

  /* ================= FETCH DASHBOARD ================= */
  useEffect(() => {
    if (!apiUrl) return;

    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const res = await fetch(apiUrl);
        const json = await res.json();

        if (!res.ok || !json.success) {
          throw new Error("Failed to fetch dashboard data");
        }

        setData(json);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [apiUrl]);

  /* ================= KPI DERIVATION ================= */
  const kpis = useMemo(() => {
    if (!data) {
      return {
        totalAssigned: 0,
        connected: 0,
        notConnected: 0,
        usingProduct: 0,
        notUsingProduct: 0,
        totalSales: 0,
        outstanding: 0,
      };
    }

    return {
      totalAssigned: data.assigned_retailers || 0,
      connected: data.connected_retailers?.connected || 0,
      notConnected: data.connected_retailers?.not_connected || 0,
      usingProduct: data.using_product?.using || 0,
      notUsingProduct: data.using_product?.not_using || 0,
      totalSales: data.sales?.this_month || 0,
      outstanding: data.sales?.outstanding || 0,
    };
  }, [data]);

  return (
    <div className="space-y-4">
      <SectionCard title="Summary">
        <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
          <li>
            You manage{" "}
            <span className="font-semibold text-slate-800">
              {loading ? <InlineLoader /> : kpis.totalAssigned} retailers
            </span>
            .
          </li>

          <li>
            Connected retailers:{" "}
            <span className="font-semibold text-emerald-700">
              {loading ? <InlineLoader /> : kpis.connected}
            </span>{" "}
            | Not connected:{" "}
            <span className="font-semibold text-rose-700">
              {loading ? <InlineLoader /> : kpis.notConnected}
            </span>
            .
          </li>

          <li>
            Product adoption:{" "}
            <span className="font-semibold text-emerald-700">
              {loading ? <InlineLoader /> : kpis.usingProduct}
            </span>{" "}
            using |{" "}
            <span className="font-semibold text-rose-700">
              {loading ? <InlineLoader /> : kpis.notUsingProduct}
            </span>{" "}
            not using.
          </li>

          <li>
            This month sales:{" "}
            <span className="font-semibold text-blue-700">
              ₹
              {loading ? (
                <InlineLoader width="w-10" />
              ) : (
                (kpis.totalSales / 1000).toFixed(1)
              )}
              k
            </span>{" "}
            | Outstanding:{" "}
            <span className="font-semibold text-amber-700">
              ₹
              {loading ? (
                <InlineLoader width="w-10" />
              ) : (
                (kpis.outstanding / 1000).toFixed(1)
              )}
              k
            </span>
            .
          </li>

          <li>
            Focus on connecting{" "}
            <span className="font-semibold">
              {loading ? <InlineLoader /> : kpis.notConnected} retailers
            </span>{" "}
            and increasing product adoption.
          </li>
        </ul>
      </SectionCard>
    </div>
  );
}



// import { KpiCard } from "../../../Component/OperationComponents/KpiCard";
// import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
// import React, { useMemo } from "react";

// // ---------------- Mock Retailers ----------------
// const mockRetailers = [
//   {
//     id: 1,
//     name: "Shree Mobile Store",
//     isActive: true,
//     isConnected: true,
//     usingProduct: true,
//     totalSales: 450000,
//     monthSales: 75000,
//     outstanding: 15000,
//   },
//   {
//     id: 2,
//     name: "Sai Telecom",
//     isActive: true,
//     isConnected: false,
//     usingProduct: false,
//     totalSales: 120000,
//     monthSales: 12000,
//     outstanding: 5000,
//   },
//   {
//     id: 3,
//     name: "Om Electronics",
//     isActive: false,
//     isConnected: false,
//     usingProduct: false,
//     totalSales: 80000,
//     monthSales: 0,
//     outstanding: 0,
//   },
// ];

// // ---------------- Mock Tasks ----------------
// const mockTasks = [
//   { id: 101, status: "Done" },
//   { id: 102, status: "Open" },
//   { id: 103, status: "Open" },
//   { id: 104, status: "Done" },
// ];

// export default function ReportsView() {
//   const retailers = mockRetailers;
//   const tasks = mockTasks;

//   // ---------------- KPI Calculations ----------------
//   const kpis = useMemo(() => {
//     const totalAssigned = retailers.length;
//     const active = retailers.filter((r) => r.isActive).length;
//     const inactive = totalAssigned - active;

//     const connected = retailers.filter((r) => r.isConnected).length;
//     const usingProduct = retailers.filter((r) => r.usingProduct).length;

//     const totalSales = retailers.reduce((sum, r) => sum + r.totalSales, 0);
//     const outstanding = retailers.reduce((sum, r) => sum + r.outstanding, 0);

//     const doneTasks = tasks.filter((t) => t.status === "Done").length;
//     const openTasks = tasks.length - doneTasks;

//     return {
//       totalAssigned,
//       active,
//       inactive,
//       connected,
//       usingProduct,
//       totalSales,
//       outstanding,
//       doneTasks,
//       openTasks,
//     };
//   }, [retailers, tasks]);

//   return (
//     <div className="space-y-4">
//       {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//         <KpiCard
//           label="My Completed Tasks"
//           value={kpis.doneTasks}
//           subLabel={`${kpis.openTasks} open`}
//         />

//         <KpiCard
//           label="Retailer Coverage"
//           value={`${kpis.active}/${kpis.totalAssigned}`}
//           subLabel="Active retailers"
//         />

//         <KpiCard
//           label="Collection Efficiency"
//           value={
//             kpis.totalSales
//               ? `${Math.round(
//                   ((kpis.totalSales - kpis.outstanding) /
//                     kpis.totalSales) *
//                     100
//                 )}%`
//               : "0%"
//           }
//           subLabel="Collections vs sales"
//         />

//         <KpiCard
//           label="Product Adoption"
//           value={
//             kpis.totalAssigned
//               ? `${Math.round(
//                   (kpis.usingProduct / kpis.totalAssigned) * 100
//                 )}%`
//               : "0%"
//           }
//           subLabel="Retailers using our product"
//         />
//       </div> */}

//       <SectionCard title="Summary">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             You manage{" "}
//             <span className="font-semibold text-slate-800">
//               {kpis.totalAssigned} retailers
//             </span>{" "}
//             —{" "}
//             <span className="font-semibold text-emerald-700">
//               {kpis.active} active
//             </span>{" "}
//             &{" "}
//             <span className="font-semibold text-rose-700">
//               {kpis.inactive} inactive
//             </span>
//             .
//           </li>

//           <li>
//             Total sales:{" "}
//             <span className="font-semibold text-blue-700">
//               ₹{(kpis.totalSales / 1000).toFixed(1)}k
//             </span>{" "}
//             | Outstanding:{" "}
//             <span className="font-semibold text-amber-700">
//               ₹{(kpis.outstanding / 1000).toFixed(1)}k
//             </span>
//             .
//           </li>

//           <li>
//             Tasks completed:{" "}
//             <span className="font-semibold text-emerald-700">
//               {kpis.doneTasks}
//             </span>{" "}
//             | Pending:{" "}
//             <span className="font-semibold text-amber-700">
//               {kpis.openTasks}
//             </span>
//           </li>

//           <li>
//             Focus on win-back of{" "}
//             <span className="font-semibold">
//               {kpis.inactive} inactive retailers
//             </span>{" "}
//             and connect{" "}
//             <span className="font-semibold">
//               {retailers.filter((r) => !r.isConnected).length} not connected
//             </span>{" "}
//             retailers.
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }



// import { KpiCard } from "../../../Component/OperationComponents/KpiCard";
// import { SectionCard } from "../../../Component/OperationComponents/SectionCard";

// export default function ReportsView({ kpis, tasks, retailers }) {
//   const completedTasks = tasks.filter((t) => t.status === "Done").length;
//   const openTasks = tasks.length - completedTasks;

//   return (
//     <div className="space-y-4">
//       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//         <KpiCard
//           label="My Completed Tasks"
//           value={completedTasks}
//           subLabel={`${openTasks} open`}
//         />
//         <KpiCard
//           label="Retailer Coverage"
//           value={`${kpis.active}/${kpis.totalAssigned}`}
//           subLabel="Active retailers"
//         />
//         <KpiCard
//           label="Collection Efficiency"
//           value={
//             kpis.totalSales
//               ? `${Math.round(
//                   ((kpis.totalSales - kpis.outstanding) / kpis.totalSales) *
//                     100
//                 )}%`
//               : "0%"
//           }
//           subLabel="Collections vs sales"
//         />
//         <KpiCard
//           label="Product Adoption"
//           value={
//             kpis.totalAssigned
//               ? `${Math.round(
//                   (kpis.usingProduct / kpis.totalAssigned) * 100
//                 )}%`
//               : "0%"
//           }
//           subLabel="Retailers using our product"
//         />
//       </div>

//       <SectionCard title="Summary">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             You manage{" "}
//             <span className="font-semibold text-slate-800">
//               {kpis.totalAssigned} retailers
//             </span>{" "}
//             out of which{" "}
//             <span className="font-semibold text-emerald-700">
//               {kpis.active} are active
//             </span>{" "}
//             and{" "}
//             <span className="font-semibold text-rose-700">
//               {kpis.inactive} inactive
//             </span>
//             .
//           </li>
//           <li>
//             Total assigned sales:{" "}
//             <span className="font-semibold text-blue-700">
//               ₹{(kpis.totalSales / 1000).toFixed(1)}k
//             </span>{" "}
//             with outstanding of{" "}
//             <span className="font-semibold text-amber-700">
//               ₹{(kpis.outstanding / 1000).toFixed(1)}k
//             </span>
//             .
//           </li>
//           <li>
//             You have completed{" "}
//             <span className="font-semibold text-emerald-700">
//               {completedTasks}
//             </span>{" "}
//             tasks and{" "}
//             <span className="font-semibold text-amber-700">
//               {openTasks}
//             </span>{" "}
//             tasks are still pending.
//           </li>
//           <li>
//             Focus areas: win-back{" "}
//             <span className="font-semibold">
//               {retailers.filter((r) => !r.isActive).length} inactive retailers
//             </span>{" "}
//             and connect{" "}
//             <span className="font-semibold">
//               {retailers.filter((r) => !r.isConnected).length} not connected
//               retailers
//             </span>{" "}
//             to the platform.
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }